import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CaptchaWidget = ({ onVerify }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [siteKey, setSiteKey] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const retryCountRef = useRef(0);

  useEffect(() => {
    axios.get(`${API}/auth/captcha-key`).then((res) => {
      setSiteKey(res.data.site_key);
      setEnabled(res.data.enabled);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!enabled || !siteKey) return;

    if (window.turnstile) {
      setScriptLoaded(true);
      return;
    }

    // Remove any existing script to avoid duplicates
    const existing = document.querySelector('script[src*="turnstile"]');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => {
      // Retry once after 2s
      setTimeout(() => {
        const retry = document.createElement('script');
        retry.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        retry.async = true;
        retry.onload = () => setScriptLoaded(true);
        document.head.appendChild(retry);
      }, 2000);
    };
    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [enabled, siteKey]);

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !siteKey) return;

    // Wait for turnstile to be available with retries
    if (!window.turnstile) {
      if (retryCountRef.current < 10) {
        retryCountRef.current++;
        setTimeout(renderWidget, 500);
      }
      return;
    }

    if (widgetIdRef.current !== null) {
      try { window.turnstile.remove(widgetIdRef.current); } catch {}
      widgetIdRef.current = null;
    }

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: 'dark',
        callback: (token) => onVerify(token),
        'expired-callback': () => onVerify(null),
        'error-callback': () => onVerify(null),
        retry: 'auto',
        'retry-interval': 3000,
      });
    } catch {}
  }, [siteKey, onVerify]);

  useEffect(() => {
    if (scriptLoaded && enabled && siteKey) {
      retryCountRef.current = 0;
      const timer = setTimeout(renderWidget, 200);
      return () => clearTimeout(timer);
    }
  }, [scriptLoaded, enabled, siteKey, renderWidget]);

  if (!enabled || !siteKey) return null;

  return (
    <div className="flex justify-center" data-testid="turnstile-widget">
      <div ref={containerRef} />
    </div>
  );
};

export default CaptchaWidget;
